import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';

/**
 * Çoktan seçmeli filtre paneli bileşeni
 * 
 * @param {Object} filters - Filtre state'i
 * @param {Function} setFilters - Filtre setter'ı
 * @param {Array} filterConfig - Filtre tanımları
 * @param {number} totalCount - Toplam kayıt sayısı
 * @param {number} filteredCount - Filtrelenmiş kayıt sayısı
 * @param {Function} resetFilters - Filtreleri sıfırlama fonksiyonu
 * 
 * filterConfig format:
 * [
 *   { key: 'yil', label: 'Yıl', type: 'multi', options: [{v:'2025',l:'2025'}] },
 *   { key: 'isin_adi', label: 'İş Adı', type: 'text' },
 *   { key: 'tutar_min', label: 'Min Tutar', type: 'number' },
 * ]
 */
export const FilterPanel = ({ filters, setFilters, filterConfig, totalCount, filteredCount, resetFilters }) => {
  const [open, setOpen] = useState(false);

  const activeCount = Object.entries(filters).reduce((count, [key, val]) => {
    if (Array.isArray(val)) return count + val.length;
    if (typeof val === 'string' && val) return count + 1;
    return count;
  }, 0);

  const toggleFilter = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: prev[key].includes(value) ? prev[key].filter(v => v !== value) : [...prev[key], value]
    }));
  };

  // Metin filtrelerini ayır
  const textFilters = filterConfig.filter(f => f.type === 'text' || f.type === 'number');
  const multiFilters = filterConfig.filter(f => f.type === 'multi');

  return (
    <Card className="bg-gray-900 border-gray-800 mb-4">
      <CardContent className="pt-4 pb-3">
        <div className="flex items-center justify-between mb-3">
          <Button onClick={() => setOpen(!open)} variant="ghost" className="text-gray-300 hover:text-white px-3 h-8">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" /></svg>
            Filtreleme {activeCount > 0 && `(${activeCount})`}
            <svg className={`w-4 h-4 ml-1 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
          </Button>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">{filteredCount} / {totalCount} kayıt</span>
            {activeCount > 0 && (
              <Button onClick={resetFilters} size="sm" variant="ghost" className="text-red-400 hover:text-red-300 h-7 text-xs">Filtreleri Temizle</Button>
            )}
          </div>
        </div>

        {/* Metin arama - her zaman görünür */}
        {textFilters.length > 0 && (
          <div className={`grid gap-3 mb-2`} style={{ gridTemplateColumns: `repeat(${Math.min(textFilters.length, 4)}, 1fr)` }}>
            {textFilters.map(f => (
              <Input key={f.key} type={f.type === 'number' ? 'number' : 'text'}
                placeholder={f.placeholder || `${f.label} ara...`}
                value={filters[f.key] || ''}
                onChange={(e) => setFilters(prev => ({...prev, [f.key]: e.target.value}))}
                className="bg-gray-800 border-gray-700 text-white text-xs h-8" />
            ))}
          </div>
        )}

        {/* Çoktan seçmeli filtreler - açılır panel */}
        {open && multiFilters.length > 0 && (
          <div className="space-y-3 pt-2 border-t border-gray-800">
            {multiFilters.map(f => (
              <div key={f.key}>
                <p className="text-xs text-gray-400 mb-1.5 font-semibold">{f.label}</p>
                <div className="flex flex-wrap gap-1.5">
                  {(f.options || []).map(opt => (
                    <Button key={opt.v} size="sm" onClick={() => toggleFilter(f.key, opt.v)}
                      className={`h-6 px-2.5 text-xs ${(filters[f.key] || []).includes(opt.v) ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
                    >{opt.l}</Button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
